<div
  class="heading skin-detail-heading"
  (resize)="handleResize($event)"
>
  @if (canGoBack()) {
    <app-button2
      appearance="secondary-ghost"
      size="s"
      ariaLabel="Go back"
      (click)="handleGoBack()"
    >
      <app-icon name="chevron-left" />
    </app-button2>
  }

  <app-input
    class="input"
    appearance="sapphire-inline"
    [inputFormControl]="form.controls.title"
    placeholder="New Notebook"
    [errorPlaceholder]="'Add title'"
    [withErrors]="submitted"
    size="s"
  />

  <div class="icons">
    <div [appPreventDirtyClick]="dirtyFormRef" class="contents">
      <!-- [MUST-002] 'Bug' debug quick-action hidden per task; uncomment to re-enable
      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="bug"
        [active]="selectedTab === 'debug'"
        matTooltip="Debug"
        (onClick)="selectedTab = 'debug'"
      />
      -->

      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="sapphire-archive2"
        [active]="form.controls.archived.value"
        [matTooltip]="form.controls.archived.value ? Labels.RestoreFromArchive : Labels.Archive"
        [disabled]="!notebook?.id"
        (onClick)="archive()"
      />

      <stch-trash-action *ngIf="!contracted" [disabled]="!notebook?.id" [deleted]="form.controls.deleted.value" (onClick)="delete()" />

<!-- SBUX-785 v1: containers are not shareable (only leaf entities). Re-enable later.
      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="sapphire-share"
        matTooltip="Share"
        [disabled]="!notebook?.id"
        [stchShareContextMenu]="notebook"
        [stchShareContextMenuDisabled]="!notebook?.id"
      />
      -->

      <app-button
        appearance="sapphire-secondary-ghost"
        size="xs"
        icon="sapphire-ellipsis"
        matTooltip="More options"
        ariaLabel="More options"
        [disabled]="!notebook?.id"
        (appNotebookContextMenuDetails)="selectedTab = 'details'"
        [appNotebookContextMenu]="notebook"
        [appNotebookContextMenuDisabled]="!notebook?.id"
        appNotebookContextMenuMode="popover"
        [printEnabled]="true"
      />
    </div>

    @if (showCloseButton) {
      <app-button
        appearance="sapphire-secondary-ghost"
        size="xs"
        icon="sapphire-close"
        ariaLabel="Close"
        (click)="handleClose()"
      />
    }
  </div>
</div>

<app-stitch-timestamps class="block" [createdAt]="notebook?.createdAt" [updatedAt]="notebook?.updatedAt" />

<!--
  #1016 r2 rows 2a-2c; renders only inside an accepted team, as the note form's does, and placed below
  the heading for the same reason — the dynamic panel's back arrow must stay the first thing in the
  panel (SBUX-23, `dp-form-back-arrow.spec.ts`).
-->
@if (!notebook?.id && workspaceIdentity(); as identity) {
  <app-tenancy-selector
    [spaceTitle]="identity.spaceTitle"
    [spaceTenantId]="identity.spaceTenantId"
    [memberTenantId]="identity.memberTenantId"
    itemNoun="notebook"
    [locked]="!!notebook?.accountId"
    [lockedReason]="notebook?.accountId ? 'This draft already belongs to a workspace.' : null"
    [(tenantId)]="sharingTenantId"
  />
}

<div class="tabs skin-detail-tabs">
  <app-tabs
    appearance="sapphire"
    [values]="tabsWithCounts()"
    [(value)]="selectedTab"
    size="m"
  />

  <div class="filler"></div>

  <!-- [MUST-001] 'Assign' button hidden per task; uncomment to re-enable

  <app-button appearance="sapphire-secondary" size="xs" label="Assign" />

  -->
  <stch-knowledge-button
    [active]="displayKnowledge"
    (click)="showKnowledge()"
  />
</div>

<div
  *ngIf="form"
  class="form"
  appDroppable
  appDroppableHoverClass="drag-over"
  [appDroppablePredicate]="dndPredicate"
  (appDroppableDrop)="dndDrop($event)"
>
  <div class="drop-area-border"></div>

  @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
    <app-entity-hub-search [contactId]="notebook?.id" />
  }

  <div class="form-heading">
    <div class="form-heading-nav">
      <div class="form-heading-nav-separator"></div>

    </div>

    <div
      *ngIf="displayKnowledge"
      class="form-heading-nav-underline"
    ></div>

    <!-- One Knowledge box, rows in the skin's order: Knots · Tags · Connections. On the Stitch tab the rows are
         the knowledge hub's facets (#2481), read-only, and hidden without hub access. -->
    <div
      class="form-knowledge"
      [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
    >
      <app-knots
        class="form-knots"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="notebook | toArray"
        [tenantId]="notebook.accountId ?? sharingTenantId()"
        [control]="form.controls.knots"
        [stateKey]="StateKey.knotsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="notebook?.id"
      />

      <app-tags
        class="form-tags"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="[notebook]"
        [tenantId]="notebook.accountId ?? sharingTenantId()"
        [control]="form.controls.tags"
        [stateKey]="StateKey.tagsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="notebook?.id"
      />

      <app-connections
        class="form-knots"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="[notebook]"
        [control]="form.controls.connections"
        [stateKey]="StateKey.connectionsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="notebook?.id"
      />
    </div>
  </div>

  <!-- stitch-app-shell ContainerItems: the list's sort & filter on a bar of its own right above the list, on
       the far right over a 1px line, so it reads as the list's (Johanna, 2026-10-21; it sat in the tab row). -->
  <div class="container-list-bar" *ngIf="selectedTab === 'notebook'">
    <app-context-menu-state-indicator svgIcon="filters" appNotesListContextMenu />
  </div>
  <app-scroll
    *ngIf="(selectedTab !== 'stitch' || (permissionsLoaded() && !hasKnowledgeHubAccess()))"
    [ngSwitch]="selectedTab"
    class="form-scroll"
    (top)="displayScrollShadow = !$event"
  >
    <div *ngIf="displayScrollShadow" class="form-scroll-shadow"></div>

    <ng-template #unsavedNotebookNotesTpl>
      <app-no-content-stub imageSrc="assets/placeholders/placeholder-notes.svg">
        <div class="flex max-w-full flex-col items-center gap-2">
          <p class="text-base font-medium text-gray-900 text-center">No notes</p>
          <p class="text-sm text-gray-600 text-center">Save your notebook to start adding notes</p>
        </div>
      </app-no-content-stub>
    </ng-template>

    <app-notes-list
      *ngSwitchCase="'notebook'"
      class="h-full"
      [filters]="notesFilters"
      [tenantItems]="[stitchItem]"
      [notFoundTemplate]="notebook?.id ? undefined : unsavedNotebookNotesTpl"
      (openNote)="handleClickItem($event)"
      (clickNote)="handleClickItem($event)"
      (doubleClickNote)="handleDblClickItem($event)"
    />

    <div
      *ngSwitchCase="'details'"
      class="form-kit"
      [class.contracted]="contracted"
    >
      <section class="form-kit-section">
        @if(false) { <!-- HIDDEN FOR VERSION 1-->
          <div class="form-kit-field">
            <span class="form-kit-label">Parent Notebook</span>
            <app-autocomplete-input
              appearance="sapphire-outline"
              placeholder="Parent Notebook"
              [suggestions]="parentSuggestions"
              [inputFormControl]="form.controls.parentId"
              size="s"
              [withErrors]="submitted"
              [validationMessages]="{
                circular: 'Circular dependency',
                deep: 'Too deep'
              }"
              [withClear]="true"
            />
          </div>
        }
      </section>

      <section class="form-kit-section">
        <stch-item-description-field
          [control]="form.controls.description"
          [item]="notebook"
          [tagsControl]="form.controls['tags']"
          [knotsControl]="form.controls['knots']"
        />
      </section>
    </div>

    <app-attachments-list
      class="form-attachments"
      *ngSwitchCase="'attachments'"
      [stitchItem]="stitchItem"
      [inputControl]="form.controls.uploads"
      [contextMenuStateVariant]="attachmentsMenuStateVariant"
      (listStateChange)="attachmentsListState = $event"
      (onClickItem)="handleClickItem($event)"
      (onDblClickItem)="handleDblClickItem($event)"
      (uploading)="onUploadingChange($event)"
    />

    <app-context-menu-state-indicator
      *ngSwitchCase="'stitch'"
      class="block ml-auto w-fit mt-5 mr-4"
      svgIcon="filters"
      appStitchListContextMenu
      (appStitchListContextMenuChange)="stitchListState = $event"
    />

    <app-linked-info-list
      *ngSwitchCase="'stitch'"
      class="form-linked-info"
      [stitch]="notebook"
      [state]="stitchListState"
      [placeholderSubtitle]="notebook?.id ? undefined : 'Save to start Stitching items'"
      (clickStitchItem)="handleClickItem($event)"
      (dblClickStitchItem)="handleDblClickItem($event)"
    />

    <stch-comments
      *ngSwitchCase="'comments'"
      class="form-linked-info"
      [stitch]="notebook"
    />

    <stch-json-viewer
      *ngSwitchCase="'debug'"
      [json]="notebook?.debug"
    />
  </app-scroll>

  @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
    <app-stitch-hub
      [stitch]="notebook"
      (clickStitchItem)="handleClickItem($event)"
      (dblClickStitchItem)="handleDblClickItem($event)"
    />
  }

  <stch-save-cancel-panel
    [enabled]="!pristine"
    [uploading]="isUploading()"
    [saveLabel]="saveLabel()"
    (onCancel)="handleCancel()"
    (onSave)="handleSubmit()"
  />

</div>

<!-- stitch-app-shell breadcrumbs footer in the main view, as on every detail page. -->
@if (navigation.enabled && stitchItem?.id) {
  <app-detail-breadcrumbs
    [crumbs]="breadcrumbs()"
    [status]="form.controls.archived.value ? 'Archived' : form.controls.deleted.value ? 'Trash' : undefined"
  />
}
